export const BANDS = [31, 62, 125, 250, 500, 1000, 2000, 4000, 8000, 16000];
export const GAIN_RANGE = 12;
export const PRESETS = {
  Flat: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  Rock: [5, 4, 3, 1, -1, -1, 1, 3, 4, 5],
  Pop: [-1, 1, 3, 4, 3, 0, -1, -1, 1, 2],
  Jazz: [4, 3, 1, 2, -2, -2, 0, 1, 3, 4],
  Classical: [4, 3, 2, 1, -1, -1, 0, 2, 3, 4],
  "Hip-Hop": [6, 5, 2, 3, -1, -1, 1, 0, 2, 3],
  Dance: [5, 6, 3, 0, 1, 3, 5, 4, 3, 0],
  Acoustic: [4, 3, 1, 1, 2, 2, 3, 3, 3, 2],
  Vocal: [-2, -3, -2, 1, 4, 4, 3, 1, 0, -1],
  Electronic: [5, 4, 1, 0, -2, 2, 1, 1, 4, 5],
};
export const DEFAULT_EQ = { enabled: true, preset: "Flat", gains: PRESETS.Flat, bass: 0, treble: 0, preamp: 0, custom: {} };
const KEY = "rh.eq";

export const loadEq = () => {
  try { const v = JSON.parse(localStorage.getItem(KEY) || "null"); return v ? { ...DEFAULT_EQ, ...v, gains: v.gains?.length === 10 ? v.gains : PRESETS.Flat } : DEFAULT_EQ; }
  catch { return DEFAULT_EQ; }
};
export const saveEq = (eq) => localStorage.setItem(KEY, JSON.stringify(eq));
export const presetGains = (eq, name) => (PRESETS[name] || eq.custom[name] || null);
/* headroom so a +12 dB band can't clip: pull the preamp down by the largest boost */
export const headroomDb = (eq) => Math.min(0, -(Math.max(0, ...eq.gains, eq.bass, eq.treble) * 0.6));

const graph = { ctx: null, sources: new WeakMap(), chain: null };

function buildChain(ctx) {
  const pre = ctx.createGain();
  const filters = BANDS.map((f, i) => {
    const b = ctx.createBiquadFilter();
    b.type = i === 0 ? "lowshelf" : i === BANDS.length - 1 ? "highshelf" : "peaking";
    b.frequency.value = f;
    b.Q.value = 1.1;
    return b;
  });
  const bass = ctx.createBiquadFilter(); bass.type = "lowshelf"; bass.frequency.value = 110;
  const treble = ctx.createBiquadFilter(); treble.type = "highshelf"; treble.frequency.value = 6000;
  const out = ctx.createGain();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.82;
  [pre, ...filters, bass, treble, out, analyser].reduce((a, b) => { a.connect(b); return b; }).connect(ctx.destination);
  return { pre, filters, bass, treble, out, analyser };
}

/* Route every deck through one shared EQ chain. Must be called from a user gesture the first time (AudioContext policy). */
export function attachDecks(decks) {
  if (!graph.ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    graph.ctx = new AC();
    graph.chain = buildChain(graph.ctx);
  }
  for (const el of decks) {
    if (graph.sources.has(el)) continue;
    try { graph.sources.set(el, graph.ctx.createMediaElementSource(el)); graph.sources.get(el).connect(graph.chain.pre); }
    catch { /* already attached elsewhere */ }
  }
  if (graph.ctx.state === "suspended") graph.ctx.resume().catch(() => {});
  return true;
}

export function applyEq(eq) {
  const c = graph.chain;
  if (!c) return;
  const t = graph.ctx.currentTime, ramp = 0.05;
  const on = eq.enabled;
  c.filters.forEach((b, i) => b.gain.setTargetAtTime(on ? eq.gains[i] : 0, t, ramp));
  c.bass.gain.setTargetAtTime(on ? eq.bass : 0, t, ramp);
  c.treble.gain.setTargetAtTime(on ? eq.treble : 0, t, ramp);
  const db = on ? eq.preamp + headroomDb(eq) : 0;
  c.pre.gain.setTargetAtTime(Math.pow(10, db / 20), t, ramp);
}

export const eqActive = () => !!graph.ctx;

/* Log-spaced spectrum levels (0..1) for `bars` bars, or null when the audio graph isn't running yet. */
let _buf = null;
export function spectrum(bars) {
  const an = graph.chain?.analyser;
  if (!an || graph.ctx.state !== "running") return null;
  if (!_buf || _buf.length !== an.frequencyBinCount) _buf = new Uint8Array(an.frequencyBinCount);
  an.getByteFrequencyData(_buf);
  const nyq = graph.ctx.sampleRate / 2, binHz = nyq / _buf.length;
  const lo = 40, hi = Math.min(16000, nyq);
  const out = new Array(bars);
  for (let i = 0; i < bars; i++) {
    const f0 = lo * Math.pow(hi / lo, i / bars), f1 = lo * Math.pow(hi / lo, (i + 1) / bars);
    const b0 = Math.max(0, Math.floor(f0 / binHz)), b1 = Math.max(b0 + 1, Math.ceil(f1 / binHz));
    let peak = 0;
    for (let b = b0; b < b1 && b < _buf.length; b++) peak = Math.max(peak, _buf[b]);
    out[i] = peak / 255;
  }
  return out;
}
